import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Database, ShieldCheck, CheckCircle2, Building, UserCheck, Briefcase } from "lucide-react";

const LoginPage = () => {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("company_admin");
  const [loading, setLoading] = useState(false);
  const [dbStatus, setDbStatus] = useState<"connected" | "checking" | "offline">("checking");
  const { user, signIn, signUp, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user) {
      if (!user.company && user.role !== "super_admin") {
        navigate("/dashboard/onboarding", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    let active = true;
    fetch("/api/health")
      .then((r) => r.json())
      .then((d) => {
        if (active) {
          setDbStatus(d?.mysql === "connected" ? "connected" : "offline");
        }
      })
      .catch(() => {
        if (active) setDbStatus("offline");
      });

    return () => {
      active = false;
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const cleanEmail = email.trim();
    if (!cleanEmail || !password) {
      toast.error("Please enter your email and password");
      return;
    }

    setLoading(true);
    try {
      const authUser = await signIn(cleanEmail, password);
      toast.success("Welcome back!");
      if (authUser && !authUser.company && authUser.role !== "super_admin") {
        navigate("/dashboard/onboarding", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const cleanName = name.trim();
    const cleanEmail = email.trim();
    if (!cleanName) {
      toast.error("Please enter your name");
      return;
    }
    if (!cleanEmail || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      await signUp(cleanEmail, password, { name: cleanName, role });
      toast.success("Account created successfully!");
      navigate("/dashboard/onboarding", { replace: true });
    } catch (error: any) {
      toast.error(error.message || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (demoEmail: string, demoPass: string) => {
    if (loading) return;
    const cleanEmail = demoEmail.trim();
    setEmail(cleanEmail);
    setPassword(demoPass);
    setLoading(true);
    try {
      const authUser = await signIn(cleanEmail, demoPass);
      toast.success(`Logged in as ${cleanEmail}`);
      if (authUser && !authUser.company && authUser.role !== "super_admin") {
        navigate("/dashboard/onboarding", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error: any) {
      toast.error(error.message || "Quick login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md mx-auto bg-card border border-border rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-3 shadow-soft">
            <span className="text-primary-foreground font-bold text-xl font-display">A</span>
          </div>
          <h1 className="text-2xl font-bold font-display text-foreground tracking-tight">AccouPro</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Cloud Accounting, GST & ERP</p>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            {dbStatus === "connected" ? (
              <Badge variant="outline" className="text-[11px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30 font-medium">
                <Database className="w-3 h-3 mr-1 inline" /> MySQL Connected
              </Badge>
            ) : dbStatus === "checking" ? (
              <Badge variant="outline" className="text-[11px] bg-muted/50 text-muted-foreground border-border font-medium">
                <Database className="w-3 h-3 mr-1 inline" /> Checking Backend...
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[11px] bg-amber-500/10 text-amber-600 border-amber-500/30 font-medium">
                <Database className="w-3 h-3 mr-1 inline" /> API Reconnecting...
              </Badge>
            )}
          </div>
        </div>

        <Tabs value={mode} onValueChange={(v) => setMode(v as any)} className="w-full">
          <TabsList className="grid grid-cols-2 mb-6">
            <TabsTrigger value="signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup">Create Account</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">
                  Email Address
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <Label htmlFor="login-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Password
                  </Label>
                </div>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 mt-2 shadow-soft font-medium"
              >
                {loading ? "Signing in..." : "Sign In to Dashboard"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <Label htmlFor="signup-name" className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">
                  Full Name
                </Label>
                <Input
                  id="signup-name"
                  type="text"
                  placeholder="Rajesh Kumar"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="signup-email" className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">
                  Email Address
                </Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="signup-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">
                  Password
                </Label>
                <Input
                  id="signup-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="signup-role" className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">
                  I am a
                </Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger id="signup-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company_admin">Business Owner / Company Admin</SelectItem>
                    <SelectItem value="ca">Chartered Accountant (CA)</SelectItem>
                    <SelectItem value="accountant">Staff Accountant</SelectItem>
                    <SelectItem value="super_admin">System Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 mt-2 shadow-soft font-medium"
              >
                {loading ? "Creating Account..." : "Create Free Account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <p className="text-center text-xs text-muted-foreground mt-6">
          <Link to="/" className="text-primary hover:underline font-medium">
            ← Back to Homepage
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;